import { z } from 'zod';
/** A compact person reference used everywhere a name and avatar is shown. */
export const PersonRef = z.object({
    id: z.uuid(),
    name: z.string(),
    initials: z.string().max(3),
    /** Index into the avatar palette, stable per person. */
    color: z.number().int().min(0).max(7),
});
/**
 * Up to two initials for an avatar. Counts characters, not UTF-16 units, so a name with an emoji or a
 * non-Latin script never yields half a character, and prefers words that start with a letter or digit
 * ("رامي حداد 🚀" → "رح"). Never longer than PersonRef allows.
 */
export function initialsOf(name) {
    const parts = name.normalize('NFC').trim().split(/\s+/).filter(Boolean);
    const words = parts.filter((w) => /^[\p{L}\p{N}]/u.test(w));
    const use = words.length ? words : parts;
    const chars = (w) => Array.from(w ?? '');
    const first = (chars(use[0])[0] ?? '?').toUpperCase();
    const last = (use.length > 1 ? (chars(use[use.length - 1])[0] ?? '') : (chars(use[0])[1] ?? '')).toUpperCase();
    return (first + last).length <= 3 ? first + last : first.slice(0, 3);
}
export function colorOf(id) {
    let h = 0;
    for (const ch of id)
        h = (h * 31 + ch.charCodeAt(0)) >>> 0;
    return h % 8;
}
/**
 * People whose name is shared with someone else in the list (case and spacing ignored), so pickers can tell
 * them apart (QA, 16 Oct 2026).
 */
export function sameNameIds(people) {
    const key = (n) => n.normalize('NFC').trim().replace(/\s+/g, ' ').toLocaleLowerCase();
    const count = new Map();
    for (const p of people)
        count.set(key(p.name), (count.get(key(p.name)) ?? 0) + 1);
    return new Set(people.filter((p) => (count.get(key(p.name)) ?? 0) > 1).map((p) => p.id));
}
/** What tells a person apart under their name: their email, else their title. Only data the caller already has. */
export function personDetail(p) {
    return p.email?.trim() || p.title?.trim() || null;
}
/** A one-line label for a plain list (a <select> option): "Sam Lee (sam@acme.test)" when the name is shared. */
export function pickerLabel(p, shared) {
    const detail = shared ? personDetail(p) : null;
    return detail ? `${p.name} (${detail})` : p.name;
}
//# sourceMappingURL=people.js.map